import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, CalendarClock, Car, LogOut, User, KeyRound, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import AuthForms from "@/components/AuthPanel";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

async function downloadReceipt(b) {
  const el = document.getElementById(`receipt-${b.reference}`);
  if (!el) return;
  try {
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#0a0a0e" });
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: [420, 600] });
    const w = pdf.internal.pageSize.getWidth();
    const h = (canvas.height / canvas.width) * w;
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, w, h);
    pdf.save(`IshuKart-Receipt-${b.reference}.pdf`);
    toast.success("Receipt डाउनलोड हो गया");
  } catch (err) {
    toast.error("Receipt बनाने में दिक्कत आई — फिर try करें");
  }
}

function BookingCard({ b }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#111116] p-6" data-testid={`account-booking-${b.reference}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono2 text-[11px] font-bold tracking-[0.12em] text-[#e6b800]">{b.reference}</p>
          <h3 className="mt-1 font-display text-lg font-bold text-white">
            IshuCar {b.model} {b.variant} <span className="text-sm font-normal text-neutral-400">· {b.color}</span>
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${b.status === "confirmed" ? "bg-[#e6b800]/15 text-[#e6b800]" : "bg-white/5 text-neutral-400"}`} data-testid={`account-booking-status-${b.reference}`}>
            {b.status === "confirmed" ? "Token paid · Confirmed" : "Payment pending"}
          </span>
          {b.founding_number ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#e6b800]/40 px-3 py-1 text-[11px] font-bold text-[#e6b800]">
              <BadgeCheck size={12} /> Founding Owner #{b.founding_number}
            </span>
          ) : null}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-neutral-400">
        <span className="flex items-center gap-1.5">
          <Car size={12} className="text-[#e6b800]" /> Token ₹{b.token_amount} {b.status === "confirmed" ? "paid" : "due"}
        </span>
        <span className="flex items-center gap-1.5">
          <CalendarClock size={12} className="text-[#e6b800]" /> Delivery: January 2028
        </span>
        {b.test_drive ? (
          <span className="flex items-center gap-1.5">
            <CalendarClock size={12} className="text-[#e6b800]" /> Test drive: {b.test_drive.city} · {b.test_drive.date} {b.test_drive.slot}
          </span>
        ) : null}
      </div>
      {b.status === "confirmed" && (
        <button
          onClick={() => downloadReceipt(b)}
          data-testid={`account-receipt-${b.reference}`}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#e6b800]/40 px-4 py-2 text-[11px] font-bold text-[#e6b800] transition-colors duration-200 hover:bg-[#e6b800]/10"
        >
          <FileDown size={12} /> Receipt डाउनलोड करें
        </button>
      )}
      {b.status === "confirmed" && (
        <div id={`receipt-${b.reference}`} style={{ position: "fixed", left: "-10000px", top: 0, width: 420 }}>
          <div style={{ background: "#0a0a0e", border: "1px solid rgba(230,184,0,0.25)", borderRadius: 16, padding: 32, color: "#ffffff", fontFamily: "Arial, sans-serif" }}>
            <div style={{ color: "#e6b800", fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 700 }}>IshuCar by IshuKart</div>
            <div style={{ fontSize: 22, fontWeight: 800, margin: "10px 0 2px", textTransform: "uppercase" }}>Token Receipt</div>
            <div style={{ color: "#9ca3af", fontSize: 12 }}>Official pre-booking payment receipt</div>
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", margin: "18px 0" }} />
            {[
              ["Reference", b.reference],
              ["Name", b.name],
              ["Email", b.email],
              ["Vehicle", `${b.model} ${b.variant} · ${b.color}`],
              ["Token amount", `₹${Number(b.token_amount).toLocaleString("en-IN")}`],
              ["Payment", `Razorpay · ${b.razorpay_order_id || "-"}`],
              ["Founding Owner", b.founding_number ? `#${b.founding_number}` : "—"],
              ["Delivery", "January 2028"],
              ["Booked on", new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ color: "#9ca3af" }}>{k}</span>
                <span style={{ fontWeight: 600, textAlign: "right" }}>{v}</span>
              </div>
            ))}
            <div style={{ marginTop: 16, fontSize: 11, color: "#9ca3af" }}>100% refundable token · support@ishukart.com</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const [user, setUser] = useState(undefined);
  const [bookings, setBookings] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api
      .get("/auth/me", { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  };
  useEffect(load, []);

  useEffect(() => {
    if (user && user.role !== "admin") {
      api
        .get("/account/bookings", { withCredentials: true })
        .then(({ data }) => setBookings(data))
        .catch(() => setBookings([]));
    }
  }, [user]);

  const logout = async () => {
    await api.post("/auth/logout", {}, { withCredentials: true });
    setUser(null);
    setBookings([]);
    toast.success("Logout हो गए");
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-28" data-testid="account-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#e6b800]/30 border-t-[#e6b800]" />
      </div>
    );
  }

  if (user && user.role === "admin") {
    return (
      <div className="mx-auto max-w-7xl px-5 pb-24 pt-40 sm:px-8" data-testid="account-admin-redirect">
        <div className="card-glow mx-auto max-w-md rounded-2xl p-10 text-center">
          <h1 className="font-display text-2xl font-extrabold uppercase text-white">Owner account</h1>
          <p className="mt-3 text-sm text-neutral-400">यह customer area है — आप owner login में हैं।</p>
          <Link to="/admin" data-testid="account-go-admin" className="mt-6 inline-block rounded-full bg-[#e6b800] px-8 py-3 font-bold text-[#070709] transition-colors duration-200 hover:bg-[#ffc700]">
            Dashboard खोलें
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-40 sm:px-8" data-testid="account-page">
      <Reveal className="text-center">
        <p className="eyebrow">My IshuCar</p>
        <h1 className="mt-4 font-display text-3xl font-extrabold uppercase text-white sm:text-5xl">
          {user ? `नमस्ते, ${user.name || "Owner"}` : <span className="text-gold-gradient">मेरी bookings</span>}
        </h1>
        <p className="mx-auto mt-4 max-w-lg text-sm text-neutral-400 sm:text-base">
          {user
            ? "आपकी सारी pre-bookings, status और delivery details — सब एक जगह।"
            : "Login करें या नया account बनाएँ — आपकी bookings और उनका status यहाँ दिखेगा।"}
        </p>
      </Reveal>

      {user ? (
        <>
          <div className="mt-8 flex justify-end gap-3">
            <button onClick={() => setPwOpen(true)} data-testid="account-change-password-button" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-neutral-300 transition-colors duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]">
              <KeyRound size={14} /> Password बदलें
            </button>
            <button onClick={logout} data-testid="account-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-neutral-300 transition-colors duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]">
              <LogOut size={14} /> Logout
            </button>
          </div>
          {bookings.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0e] p-10 text-center" data-testid="account-empty">
              <User size={28} className="mx-auto text-[#e6b800]" />
              <p className="mt-4 text-sm text-neutral-400">
                इस email पर कोई booking नहीं मिली — नीचे से अपनी IshuCar reserve कीजिए।
              </p>
              <Link to="/booking" data-testid="account-empty-book-link" className="mt-6 inline-block rounded-full bg-[#e6b800] px-7 py-3 text-sm font-bold text-[#070709] transition-colors duration-200 hover:bg-[#ffc700]">
                अपनी IshuCar बुक करें
              </Link>
            </div>
          ) : (
            <div className="mt-6 space-y-4" data-testid="account-bookings-list">
              {bookings.map((b) => (
                <BookingCard key={b.reference} b={b} />
              ))}
            </div>
          )}
          {pwOpen && (
            <ChangePasswordModal
              onClose={() => setPwOpen(false)}
              title="Change password"
              description="यह password आपके IshuKart account में login के लिए है।"
              testIdPrefix="customer"
              modalTestId="customer-change-password-modal"
            />
          )}
        </>
      ) : (
        <div className="mt-10">
          <AuthForms onAuthed={load} />
        </div>
      )}
    </div>
  );
}
